import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Mail, Phone, Building2, DollarSign, User, FileText, Edit, Bot, ExternalLink, Zap, Activity, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal, formatLocalDate } from '../lib/dateUtils';

export function LeadPreviewPanel({
  open,
  onOpenChange,
  lead,
  statusConfigs,
  currency,
  hasPermission,
  onViewDetails,
  onEmailClick,
  onCallClick,
  onAICallClick,
  onNoteClick,
  onEdit,
  aiStatus,
  aiActivities,
  aiLoading,
  onRunSalesAI,
}) {
  if (!lead) return null;

  const statusCfg = statusConfigs[lead.status] || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lead-preview-sheet">
        <div className="space-y-4">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>{lead.name}</span>
              <Button variant="ghost" size="sm" onClick={() => onViewDetails(lead.id)} data-testid="view-full-details-button">
                <ExternalLink className="h-4 w-4 mr-2" />Full Details
              </Button>
            </SheetTitle>
          </SheetHeader>

          <div>
            <Badge style={{ backgroundColor: statusCfg.bgColor, color: statusCfg.textColor }}>
              {statusCfg.label || lead.status}
            </Badge>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-5 gap-2 pb-4 border-b">
            <Button variant="outline" size="sm" className="flex-col h-auto py-3" onClick={() => onEmailClick(lead)} data-testid="preview-action-email">
              <Mail className="h-4 w-4 mb-1" /><span className="text-xs">Email</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-col h-auto py-3" onClick={() => onCallClick(lead)} data-testid="preview-action-call">
              <Phone className="h-4 w-4 mb-1" /><span className="text-xs">Call</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-col h-auto py-3" onClick={() => onAICallClick(lead)} data-testid="preview-action-ai-call">
              <Bot className="h-4 w-4 mb-1" /><span className="text-xs">AI Call</span>
            </Button>
            <Button variant="outline" size="sm" className="flex-col h-auto py-3" onClick={() => onNoteClick(lead)} data-testid="preview-action-note">
              <FileText className="h-4 w-4 mb-1" /><span className="text-xs">Note</span>
            </Button>
            {hasPermission('lead', 'kanban', 'update') && (
              <Button variant="outline" size="sm" className="flex-col h-auto py-3"
                onClick={() => { onOpenChange(false); onEdit(lead); }} data-testid="preview-action-edit">
                <Edit className="h-4 w-4 mb-1" /><span className="text-xs">Edit</span>
              </Button>
            )}
          </div>

          {/* Contact Information */}
          <Card>
            <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1"><Label className="text-xs text-muted-foreground">Email</Label><p className="text-sm">{lead.email}</p></div>
              </div>
              {lead.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1"><Label className="text-xs text-muted-foreground">Phone</Label><p className="text-sm">{lead.phone}</p></div>
                </div>
              )}
              {lead.company && (
                <div className="flex items-start gap-3">
                  <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1"><Label className="text-xs text-muted-foreground">Company</Label><p className="text-sm">{lead.company}</p></div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <DollarSign className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="flex-1"><Label className="text-xs text-muted-foreground">Expected Value</Label><p className="text-sm font-medium">{currency.format(parseFloat(lead.value || 0))}</p></div>
              </div>
              {lead.source && (
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1"><Label className="text-xs text-muted-foreground">Source</Label><p className="text-sm">{lead.source}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {lead.notes && (
            <Card>
              <CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-muted-foreground">{lead.notes}</p></CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {lead.created_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Created</span>
                  <span>{formatLocalDate(lead.created_at)}</span>
                </div>
              )}
              {lead.updated_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Updated</span>
                  <span>{formatLocalDate(lead.updated_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Actions */}
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-400" />AI Actions
                {aiStatus?.is_demo && <Badge variant="outline" className="ml-auto text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">DEMO MODE</Badge>}
                {aiStatus && !aiStatus.is_demo && aiStatus.can_execute && <Badge variant="outline" className="ml-auto text-xs bg-green-500/20 text-green-400 border-green-500/30">LIVE</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => onRunSalesAI(lead)} disabled={aiLoading || (aiStatus && !aiStatus.can_execute)} data-testid="run-sales-ai-button">
                {aiLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running Sales AI...</> : <><Zap className="h-4 w-4 mr-2" />Run Sales AI</>}
              </Button>
              {aiStatus && !aiStatus.can_execute && aiStatus.blocked_reason && (
                <p className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded">{aiStatus.blocked_reason}</p>
              )}
              {aiActivities.length > 0 && (
                <div className="pt-2 border-t border-purple-500/20">
                  <p className="text-xs font-medium mb-2 flex items-center gap-1"><Activity className="h-3 w-3" />Recent AI Activity</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {aiActivities.slice(0, 3).map((act, idx) => (
                      <div key={act.id || idx} className="text-xs bg-slate-800/50 rounded p-2">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className={`text-[10px] ${act.mode === 'SIMULATED' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}`}>{act.mode}</Badge>
                          <span className="text-muted-foreground">{act.created_at ? formatLocal(act.created_at, 'MMM dd, HH:mm') : '-'}</span>
                        </div>
                        <p className="text-muted-foreground">{act.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-2 pt-4 border-t">
            <Button className="flex-1" onClick={() => onViewDetails(lead.id)} data-testid="preview-view-details-button">View Full Details</Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="preview-close-button">Close</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
